<!-- =========================================================================================
	File Name: TheNavbar.vue
	Description: Navbar component
	Component Name: TheNavbar
========================================================================================== -->


<template>
<div class="relative">
	<div class="vx-navbar-wrapper">
		<vs-navbar class="vx-navbar navbar-custom" :color="navbarColor" :class="classObj">

			<!-- SM - OPEN SIDEBAR BUTTON -->
			<feather-icon class="sm:inline-flex xl:hidden cursor-pointer mr-1" icon="MenuIcon" @click.stop="showSidebar"></feather-icon>

			<template v-if="breakpoint != 'md'">
				<!-- STARRED PAGES - FIRST 10 -->
				<ul class="vx-navbar__starred-pages flex items-center">
          <li class="starred-page" v-for="(page, index) in starredPagesLimited" :key="index">
            <vx-tooltip :text="page.label" position="bottom" delay=".3s">
              <feather-icon svgClasses="h-6 w-6" class="p-2 cursor-pointer" :icon="page.labelIcon" @click="$router.push(page.url)"></feather-icon>
            </vx-tooltip>
          </li>
				</ul>

				<!-- STARRED PAGES MORE -->
				<div class="vx-navbar__starred-pages--more-dropdown" v-if="starredPages.length">
					<vs-dropdown vs-custom-content vs-trigger-click>
						<feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" class="cursor-pointer p-2"></feather-icon>
						<vs-dropdown-menu>
							<ul class="vx-navbar__starred-pages-more--list">
								<draggable v-model="starredPages" :group="{name: 'pinList'}" class="cursor-move">
									<li class="starred-page--more flex items-center cursor-pointer" v-for="page in starredPages" :key="page.url" @click="$router.push(page.url)">
										<feather-icon svgClasses="h-5 w-5" class="ml-2 mr-1" :icon="page.labelIcon"></feather-icon>
										<span class="px-2 pt-2 pb-1">{{ page.label }}</span>
									</li>
								</draggable>
							</ul>
						</vs-dropdown-menu>
					</vs-dropdown>
				</div>

				<div class="bookmark-container">
					<feather-icon icon="StarIcon" :svgClasses="['stoke-current text-warning', {'text-white': navbarColor != '#fff'}]" class="cursor-pointer p-2" @click.stop="showBookmarkPagesDropdown = !showBookmarkPagesDropdown" />
            <div v-click-outside="outside" class="absolute bookmark-list w-1/3 xl:w-1/4 mt-4" v-if="showBookmarkPagesDropdown">
            <vx-auto-suggest :autoFocus="true" :data="navbarSearchAndPinList" @selected="selected" @actionClicked="actionClicked" inputClassses="w-full" show-action show-pinned background-overlay></vx-auto-suggest>
					</div>
				</div>
			</template>

			<vs-spacer></vs-spacer>

			<!-- 主题 -->
			<vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer">
				<span class="cursor-pointer flex items-center mr-5 nav-change-theme-btn">
          <feather-icon :icon="getCurrentThemeData.icon" class="sm"></feather-icon>
          <span class="hidden sm:block ml-2">{{ getCurrentThemeData.name }}</span>
        </span>
				<vs-dropdown-menu class="w-48 i18n-dropdown vx-navbar-dropdown">
					<vs-dropdown-item @click="updateTheme('light')">
            <feather-icon icon="SunIcon" class="sm"></feather-icon>
            <span class="hidden sm:block ml-2">&nbsp;亮色</span>
          </vs-dropdown-item>
					<vs-dropdown-item @click="updateTheme('dark')">
            <feather-icon icon="CloudIcon" class="sm"></feather-icon>
            <span class="hidden sm:block ml-2">&nbsp;暗色</span>
          </vs-dropdown-item>
					<vs-dropdown-item @click="updateTheme('semi-dark')">
            <feather-icon icon="CloudOffIcon" class="sm"></feather-icon>
            <span class="hidden sm:block ml-2">&nbsp;半暗</span>
          </vs-dropdown-item>
				</vs-dropdown-menu>
			</vs-dropdown>

      <!-- 充值 -->
      <div class="cursor-pointer navbar-fuzzy-search mr-5 flex items-center nav-recharge-btn" @click="showRecharge">
        <feather-icon icon="DollarSignIcon"></feather-icon>
        <span>充值</span>
      </div>
      <vs-prompt
        vs-title="充值"
        vs-accept-text="确认充值"
        vs-cancel-text="取消"
        :vs-active.sync="rechargeShow"
        @vs-accept="recharge"
      >
        <vs-input class="w-full mb-3" v-model="rechargeUid" label="会员ID / 用户名" />
        <vs-input class="w-full mb-3" v-model="rechargeCount" label="充值金额 (元)" />
        <vs-input class="w-full mb-3" v-model="rechargeLimit" label="流水限制 (元)" placeholder="达到一定流水才可提现" />
        <vs-select class="w-full mb-3" v-model="rechargeType" label="账变类型">
          <vs-select-item :value="item" :text="item" v-for="(item, index) in logTypeList" :key="index" />
        </vs-select>
        <vs-input class="w-full" v-model="rechargeRemark" label="备注" />
      </vs-prompt>

      <!-- 搜索 -->
      <div class="search-full-container w-full h-full absolute left-0 rounded-lg" :class="{'flex': showFullSearch}" v-show="showFullSearch">
        <vx-auto-suggest
          class="w-full"
          inputClassses="w-full vs-input-no-border vs-input-no-shdow-focus no-icon-border"
          :autoFocus="showFullSearch"
          :data="navbarSearchAndPinList"
          icon="SearchIcon"
          placeholder="Search..."
          ref="navbarSearch"
          @closeSearchbar="showFullSearch = false"
          @selected="selected"
          background-overlay
        />
        <div class="absolute right-0 h-full z-50">
          <feather-icon icon="XIcon" class="px-4 cursor-pointer h-full close-search-icon" @click="showFullSearch = false"></feather-icon>
        </div>
      </div>

      <feather-icon icon="SearchIcon" @click="showFullSearch = true" class="cursor-pointer navbar-fuzzy-search mr-5"></feather-icon>

			<!-- NOTIFICATIONS -->
			<vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer">
				<feather-icon icon="BellIcon" class="cursor-pointer mt-1 sm:mr-6 mr-2" :badge="unreadNotificationsCount"></feather-icon>
				<vs-dropdown-menu class="notification-dropdown dropdown-custom vx-navbar-dropdown">

					<div class="notification-top text-center p-5 bg-primary text-white">
						<h3 class="text-white">{{ unreadNotificationsCount }} 最新</h3>
						<p class="opacity-75">未读程序风控通知</p>
					</div>

					<VuePerfectScrollbar ref="mainSidebarPs" class="scroll-area--nofications-dropdown p-0 mb-10" :settings="settings">
					<ul class="bordered-items">
						<li v-for="(ntf, index) in unreadNotifications" :key="ntf.index" @click="showNotificationAlert" :data-index="index" class="flex justify-between px-4 py-4 notification cursor-pointer">
							<div class="flex items-start">
								<feather-icon icon="MessageSquareIcon" :svgClasses="[`notification-color-${ntf.type}`, 'stroke-current mr-1 h-6 w-6']"></feather-icon>
								<div class="mx-2">
									<span class="font-medium block notification-title" :class="[`notification-color-${ntf.type}`]">{{ ntf.title }}</span>
									<small>{{ ntf.content }}</small>
								</div>
							</div>
							<small class="mt-1 whitespace-no-wrap">{{ elapsedTime(ntf.time) }}</small>
						</li>
					</ul>
					</VuePerfectScrollbar>
          <div class="
            checkout-footer
            fixed
            bottom-0
            rounded-b-lg
            text-primary
            w-full
            p-2
            font-semibold
            text-center
            border
            border-b-0
            border-l-0
            border-r-0
            border-solid
            d-theme-border-grey-light
            cursor-pointer"
          >
          <router-link to="/notification/list">更多通知</router-link>
        </div>
				</vs-dropdown-menu>
			</vs-dropdown>

			<!-- USER META -->
			<div class="the-navbar__user-meta flex items-center">
				<div class="text-right leading-tight hidden sm:block">
					<p class="font-semibold">{{ admin.username }}</p>
					<small>{{ admin.superAdmin == 1 ? '超级管理' : '普通管理' }}</small>
				</div>
				<vs-dropdown vs-custom-content vs-trigger-click class="cursor-pointer">
					<div class="con-img ml-3">
						<img
							key="localImg"
							:src="require('@/assets/images/portrait/small/avatar-s-11.png')"
							alt="user-img"
							width="40"
							height="40"
							class="rounded-full shadow-md cursor-pointer block"
            />
					</div>
					<vs-dropdown-menu class="vx-navbar-dropdown">
						<ul style="min-width: 9rem">
							<li
                class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white"
                @click="$router.push({ name: 'adminList', query: { id: admin.id } })">

                <feather-icon icon="UserIcon" svgClasses="w-4 h-4" />
                <span class="ml-2">编辑资料</span>
              </li>
							<li
                class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white"
                @click="startBeginnerTour">

                <feather-icon icon="HelpCircleIcon" svgClasses="w-4 h-4" />
                <span class="ml-2">界面教程</span>
              </li>

							<vs-divider class="m-1"></vs-divider>

							<li
                class="flex py-2 px-4 cursor-pointer hover:bg-primary hover:text-white"
                @click="logout">
                <feather-icon icon="LogOutIcon" svgClasses="w-4 h-4"/>
                <span class="ml-2">退出登录</span>
              </li>
						</ul>
					</vs-dropdown-menu>
				</vs-dropdown>
			</div>

		</vs-navbar>

    <notification-alert :content="unreadNotifications[unreadNotificationIndex]" @read="readNotification" ref="notificationAlert"></notification-alert>

    <audio id="notificationRemind">
      <source src="../../assets/media/notification.mp3" type="audio/mp3">
    </audio>
	</div>
</div>
</template>

<script>

import VxAutoSuggest from '@/components/vx-auto-suggest/VxAutoSuggest.vue'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import draggable from 'vuedraggable'
import navbarSearchAndPinList from '@/layouts/components/navbarSearchAndPinList'
import notificationAlert from './notificationAlert.vue'

export default {
    name: "the-navbar",
    props: {
      navbarColor: {
        type: String,
        default: '#fff',
      },
      admin: {
        type: Object,
        default() {
          return {
            username: '游客',
            superAdmin: 0
          }
        }
      }
    },
    data() {
      return {
        navbarSearchAndPinList: navbarSearchAndPinList,
        searchQuery: '',
        showFullSearch: false,
        unreadNotifications: [],
        unreadNotificationsCount: 0,
        unreadNotificationIndex: 0,
        settings: {
          maxScrollbarLength: 60,
          wheelSpeed: .60,
        },
        autoFocusSearch: false,
        showBookmarkPagesDropdown: false,
        themeList: {
          light: { icon: 'SunIcon', name: '亮色' },
          dark: { icon: "CloudIcon", name: '暗色' },
          'semi-dark': { icon: "CloudOffIcon", name: '半暗色' }
        },
        logTypeList: [],
        rechargeShow: false,
        rechargeUid: '',
        rechargeCount: '',
        rechargeRemark: '',
        rechargeLimit: '',
        rechargeType: '充值'
      }
    },
    watch: {
      '$route'() {
        if (this.showBookmarkPagesDropdown) this.showBookmarkPagesDropdown = false
      }
    },
    computed: {
      // HELPER
      sidebarWidth() {
        return this.$store.state.sidebarWidth
      },
      breakpoint() {
        return this.$store.state.breakpoint
      },

      // NAVBAR STYLE
      classObj() {
        if (this.sidebarWidth == "default") return "navbar-default"
        else if (this.sidebarWidth == "reduced") return "navbar-reduced"
        else if (this.sidebarWidth) return "navbar-full"
      },

      // 获取当前主题
      getCurrentThemeData() {
        const theme = this.$store.state.theme
        return this.themeList[theme]
      },

      // BOOKMARK & SEARCH
      data() {
        return this.$store.state.navbarSearchAndPinList
      },

      starredPages() {
        const list = []
        let store = localStorage.getItem('navStarList')
        store = store ? JSON.parse(store) : {}
        for (let i = 0; i < this.navbarSearchAndPinList.data.length; i++) {
          const item = this.navbarSearchAndPinList.data[i]
          if (store[item.label] || (typeof store[item.label] === 'undefined' && item.highlightAction)) {
            list.push(item)
          }
        }
        return list
      },

      starredPagesLimited() {
        return this.starredPages.slice(0, 10)
      }
    },
    methods: {
      startBeginnerTour() {
        this.$emit('startBeginnerTour')
      },
      showRecharge(username) {
        if (typeof username === 'string') this.rechargeUid = username
        this.rechargeShow = true
      },
      recharge() {
        const postdata = {}
        postdata.authKey = localStorage.getItem('authKey')
        postdata.uid = this.rechargeUid
        postdata.count = this.rechargeCount
        postdata.remark = this.rechargeRemark
        postdata.limit = this.rechargeLimit
        postdata.type = this.rechargeType
        this.$utils.post('money-rechargeDo', postdata).then(res => {
          this.$vs.dialog({
            title: '提示',
            text: res,
            acceptText: '确认'
          })
        })
      },
      updateTheme(theme) {
        localStorage.setItem('theme', theme)
        this.$store.dispatch('updateTheme', theme)
      },
      showSidebar() {
        this.$store.commit('TOGGLE_IS_SIDEBAR_ACTIVE', true)
      },
      selected(item) {
        this.$router.push(item.url)
        this.showFullSearch = false
      },
      actionClicked(item) {
        this.navbarSearchAndPinList.data[item.index].highlightAction = !this.navbarSearchAndPinList.data[item.index].highlightAction
        this.saveNavStarList()
      },
      showNavbarSearch() {
        this.showFullSearch = true
      },
      showSearchbar() {
        this.showFullSearch = true
      },
      elapsedTime(startTime) {
        let x = new Date(startTime)
        let now = new Date()
        let timeDiff = now - x
        timeDiff /= 1000

        let seconds = Math.round(timeDiff)
        timeDiff = Math.floor(timeDiff / 60)

        let minutes = Math.round(timeDiff % 60)
        timeDiff = Math.floor(timeDiff / 60)

        let hours = Math.round(timeDiff % 24)
        timeDiff = Math.floor(timeDiff / 24)

        let days = Math.round(timeDiff % 365)
        timeDiff = Math.floor(timeDiff / 365)

        let years = timeDiff

        if (years > 0) {
            return years + (years > 1 ? ' Years ' : ' Year ') + '前';
        } else if (days > 0) {
            return days + (days > 1 ? ' Days ' : ' Day ') + '前';
        } else if (hours > 0) {
            return hours + (hours > 1 ? ' Hrs ' : ' Hour ') + '前';
        } else if (minutes > 0) {
            return minutes + (minutes > 1 ? ' Mins ' : ' Min ') + '前';
        } else if (seconds > 0) {
            return seconds + (seconds > 1 ? `${seconds} sec ago` : '刚刚');
        }
        return '刚刚'
      },
      logout() {
        localStorage.removeItem('authKey')
        this.$router.push({ name: 'pageLogin' })
      },
      saveNavStarList() {
        localStorage.setItem('navStarList', JSON.stringify(this.navbarSearchAndPinList))
      },
      outside: function() {
        this.showBookmarkPagesDropdown = false
      },
      // 获取风控列表
      getUnreadNotificationList() {
        this.$utils.post('notification-list', { authKey: localStorage.getItem('authKey'), isRead: 0 }, null, true).then(res => {
          this.unreadNotifications = res.list
          this.unreadNotificationsCount = res.count
          if (res.count > 0) document.getElementById('notificationRemind').play()
          if (res.count == 0) document.getElementById('notificationRemind').pause()
        })
      },
      // 查看风控详情
      showNotificationAlert(e) {
        const index = e.currentTarget.dataset.index
        this.unreadNotificationIndex = index
        this.$refs.notificationAlert.show()
      },
      // 已读风控
      readNotification() {
        this.unreadNotifications.splice(this.unreadNotificationIndex, 1)
        this.unreadNotificationsCount--
      }
    },
    mounted () {
      const navStarList = localStorage.getItem('navStarList')
      if (navStarList) this.navbarSearchAndPinList = JSON.parse(navStarList)

      this.getUnreadNotificationList()
      setInterval(() => {
        this.getUnreadNotificationList()
      }, 5000)

      this.$utils.post('record-userLogType', { authKey: localStorage.getItem('authKey') }).then(res => {
        for (let i = 0; i < res.length; i++) {
          this.logTypeList.push(res[i])
        }
      })
    },
    directives: {
      'click-outside': {
        bind: function(el, binding) {
          const bubble = binding.modifiers.bubble
          const handler = (e) => {
            if (bubble || (!el.contains(e.target) && el !== e.target)) {
              binding.value(e)
            }
          }
          el.__vueClickOutside__ = handler
          document.addEventListener('click', handler)
        },

        unbind: function(el) {
          document.removeEventListener('click', el.__vueClickOutside__)
          el.__vueClickOutside__ = null
        }
      }
    },
    components: {
      VxAutoSuggest,
      VuePerfectScrollbar,
      draggable,
      notificationAlert
    }
}
</script>

<style>
  .notification-color-sameIpLogin {
    color: #2ecc71;
  }
  .notification-color-sameIpRegister {
    color: #3498db;
  }
  .notification-color-sameIpRecharge {
    color: #9b59b6;
  }
  .notification-color-sameIpCash {
    color: #f1c40f;
  }
  .notification-color-sameBankName {
    color: #e84393;
  }
  .notification-color-overCash {
    color: #e74c3c;
  }
  .notification-color-overBonus {
    color: #B33771;
  }
</style>
